import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, EyeOff, Eye, AlertTriangle, History } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import * as inspectorApi from '@/api/inspector.api';
import type { ConfigInspector } from '@/api/inspector.api';

/**
 * AJUSTES DEL INSPECTOR — qué busca, dónde mira y qué no le tiene que llegar.
 *
 * Es la pantalla que hace que el Inspector sea un producto y no el asistente de
 * una sola persona. Esteban lo pidió explícito: *"en los tenants de las demás
 * personas tiene que ser configurable... cada uno de ellos van a tener
 * necesidades diferentes."*
 *
 * ═══ LOS CANALES SE ELIGEN, NO SE ASUMEN ═══
 *
 * *"El inspector lee todas las conversaciones de mi canal solamente, o de los
 * canales que yo le asigne."* Sin canales elegidos NO lee nada — y la pantalla
 * lo dice en voz alta, porque un Inspector encendido que no lee parece roto.
 * Asumir "todos" sería leer conversaciones que el dueño no autorizó.
 *
 * ═══ EL AVISO DE LECTURA ═══
 *
 * La promesa que más le importa es que **nunca marque como visto**. Eso vive en
 * el canal (`acusarLectura`), no acá, pero se muestra acá porque es donde el
 * dueño viene a mirar: si algún canal lo tiene encendido, tiene que verlo en
 * rojo y no enterarse cuando ya pasó.
 */

const HORAS = Array.from({ length: 24 }, (_, i) => i);

export default function InspectorAjustes() {
  const toast = useToast();
  const qc = useQueryClient();
  const [borrador, setBorrador] = useState<Partial<ConfigInspector>>({});

  const datos = useQuery({
    queryKey: ['inspector', 'config'],
    queryFn: inspectorApi.getConfigInspector,
  });

  // El borrador arranca del servidor una sola vez. Si se resincronizara en cada
  // render, escribir en un campo se pisaría solo con lo que hay guardado.
  useEffect(() => {
    if (datos.data) setBorrador(datos.data.config);
  }, [datos.data]);

  const ajustarCanal = useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: Record<string, boolean> }) =>
      inspectorApi.ajustarCanal(id, cambios),
    onSuccess: (_d, v) => {
      toast.success(
        v.cambios.traerHistorial !== undefined
          ? v.cambios.traerHistorial
            ? 'Va a traer el historial la próxima vez que vincules'
            : 'Ya no traerá el historial'
          : v.cambios.acusarLectura
            ? 'Ojo: ahora marca los chats como vistos'
            : 'Ya no marca nada como visto',
      );
      void qc.invalidateQueries({ queryKey: ['inspector'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const guardar = useMutation({
    mutationFn: () => inspectorApi.guardarConfigInspector(borrador),
    onSuccess: () => {
      toast.success('Ajustes guardados.');
      void qc.invalidateQueries({ queryKey: ['inspector'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (datos.isPending) return <Spinner />;
  if (datos.isError || !datos.data) {
    return (
      <Card>
        <CardBody className="text-sm text-red-600 dark:text-red-300">
          No se pudo leer la configuración del Inspector.
        </CardBody>
      </Card>
    );
  }

  const { objetivosDisponibles, canales } = datos.data;
  const cfg = { ...datos.data.config, ...borrador };
  const set = (parcial: Partial<ConfigInspector>) => setBorrador((b) => ({ ...b, ...parcial }));

  const alternar = <T,>(lista: T[], valor: T): T[] =>
    lista.includes(valor) ? lista.filter((x) => x !== valor) : [...lista, valor];

  const canalesQueAcusan = canales.filter((c) => c.acusarLectura);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ajustes del Inspector"
        description="Qué busca, en qué canales mira y qué no te tiene que llegar."
        action={
          <Button onClick={() => guardar.mutate()} disabled={guardar.isPending}>
            <Save className="h-4 w-4" /> Guardar
          </Button>
        }
      />

      {/* Lo que rompe la promesa va arriba de todo, no enterrado en una sección. */}
      {canalesQueAcusan.length > 0 && (
        <Card>
          <CardBody className="flex items-start gap-3 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <span>
              <strong>
                {canalesQueAcusan.length === 1
                  ? 'Un canal está marcando los chats como vistos'
                  : `${canalesQueAcusan.length} canales están marcando los chats como vistos`}
              </strong>{' '}
              ({canalesQueAcusan.map((c) => c.name).join(', ')}). Se apaga desde Canales.
            </span>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={cfg.activo}
              onChange={(e) => set({ activo: e.target.checked })}
            />
            <span className="font-medium">Inspector encendido</span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Cómo se llama</span>
            <input
              className="w-full max-w-xs rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              value={cfg.nombre}
              placeholder="Athena"
              onChange={(e) => set({ nombre: e.target.value })}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Con este nombre firma los reportes que te manda por WhatsApp.
            </span>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <p className="text-sm font-semibold">Qué canales lee</p>
          {canales.length === 0 ? (
            <p className="text-sm text-slate-400">
              No hay canales todavía. Conecta tu WhatsApp en Canales y vuelve.
            </p>
          ) : (
            canales.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={cfg.canales.includes(c.id)}
                  onChange={() => set({ canales: alternar(cfg.canales, c.id) })}
                />
                <span>{c.name}</span>
                <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {c.status}
                </Badge>
                {c.simulacro && (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                    Simulacro
                  </Badge>
                )}
              </label>
            ))
          )}
        </CardBody>
      </Card>

      {/* ── CÓMO LEE CADA CANAL ──
          Los dos interruptores que deciden qué hace Athena con un WhatsApp.
          Van juntos porque se miran juntos, y con su consecuencia escrita al
          lado: un interruptor sin consecuencia visible se toca sin pensar. */}
      <Card>
        <CardBody className="space-y-3">
          <p className="text-sm font-semibold">Cómo lee cada canal</p>
          {canales.length === 0 ? (
            <p className="text-sm text-slate-400">No hay canales todavía.</p>
          ) : (
            canales.map((c) => (
              <div
                key={c.id}
                className="space-y-2 border-b border-slate-100 pb-3 last:border-0 dark:border-slate-800"
              >
                <p className="text-sm font-medium">{c.name}</p>

                <button
                  onClick={() =>
                    ajustarCanal.mutate({
                      id: c.id,
                      cambios: { acusarLectura: !c.acusarLectura },
                    })
                  }
                  disabled={ajustarCanal.isPending}
                  className="flex w-full items-start gap-2 text-left"
                >
                  <span
                    className={
                      c.acusarLectura
                        ? 'mt-0.5 text-red-600 dark:text-red-400'
                        : 'mt-0.5 text-emerald-600 dark:text-emerald-400'
                    }
                  >
                    {c.acusarLectura ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">
                      {c.acusarLectura
                        ? 'Marca los chats como vistos'
                        : 'No marca nada como visto'}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {c.acusarLectura
                        ? 'La otra persona ve los dos tiquecitos azules. Tócalo para apagarlo.'
                        : 'Nadie se entera de que Athena leyó. Es como debe estar.'}
                    </span>
                  </span>
                </button>

                <button
                  onClick={() =>
                    ajustarCanal.mutate({
                      id: c.id,
                      cambios: { traerHistorial: !c.traerHistorial },
                    })
                  }
                  disabled={ajustarCanal.isPending}
                  className="flex w-full items-start gap-2 text-left"
                >
                  <span
                    className={
                      c.traerHistorial
                        ? 'mt-0.5 text-violet-600 dark:text-violet-300'
                        : 'mt-0.5 text-slate-400'
                    }
                  >
                    <History className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">
                      {c.traerHistorial
                        ? 'Trae tus mensajes viejos al vincular'
                        : 'Solo lee los mensajes nuevos'}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {c.traerHistorial
                        ? 'WhatsApp le manda el historial reciente cuando escaneas el QR. Sigue sin marcar nada como visto.'
                        : 'Athena arranca en blanco y solo ve lo que entre desde hoy.'}
                    </span>
                  </span>
                </button>
              </div>
            ))
          )}
          {/* Vacío es vacío, nunca "todos": leer sin permiso es peor que no leer. */}
          {canales.length > 0 && cfg.canales.length === 0 && (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Sin canales elegidos el Inspector no lee nada, aunque esté encendido.
            </p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <p className="text-sm font-semibold">Qué busca</p>
          {objetivosDisponibles.map((o) => (
            <label key={o.id} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={cfg.objetivos.includes(o.id)}
                onChange={() => set({ objetivos: alternar(cfg.objetivos, o.id) })}
              />
              <span>
                <span className="font-medium">{o.nombre}</span>
                <span className="block text-xs text-slate-500">{o.descripcion}</span>
              </span>
            </label>
          ))}
          {cfg.objetivos.length === 0 && (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Sin objetivos el Inspector no sabe qué buscar y no anota nada.
            </p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <p className="text-sm font-semibold">Cuándo te reporta</p>
          <div className="flex flex-wrap gap-1">
            {HORAS.map((h) => {
              const puesta = cfg.horasDeReporte.includes(h);
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => set({ horasDeReporte: alternar(cfg.horasDeReporte, h) })}
                  className={
                    puesta
                      ? 'rounded border border-sky-500 bg-sky-500 px-2 py-1 text-xs text-white'
                      : 'rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300'
                  }
                >
                  {String(h).padStart(2, '0')}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500">
            En tu hora. Si no eliges ninguna, no te manda reportes de lapso.
          </p>

          <label className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={cfg.urgencias.activo}
              onChange={(e) =>
                set({ urgencias: { ...cfg.urgencias, activo: e.target.checked } })
              }
            />
            <span className="font-medium">Avisarme aparte lo urgente</span>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block">Cuántos minutos antes</span>
            <input
              type="number"
              min={1}
              max={1440}
              className="w-24 rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              value={cfg.urgencias.minutosDeAnticipacion}
              onChange={(e) =>
                set({
                  urgencias: {
                    ...cfg.urgencias,
                    minutosDeAnticipacion: Number(e.target.value) || 1,
                  },
                })
              }
            />
            <span className="mt-1 block text-xs text-slate-500">
              Si tienes una reunión dentro de esa ventana, te escribe sin esperar al próximo
              reporte.
            </span>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <p className="text-sm font-semibold">Qué NO te tiene que llegar</p>
          <textarea
            rows={4}
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            value={cfg.queIgnorar}
            placeholder="Ej.: nada personal ni de familia. Sólo trabajo."
            onChange={(e) => set({ queIgnorar: e.target.value })}
          />
          <p className="text-xs text-slate-500">
            Todo lo que caiga acá lo baja a ruido, por urgente que parezca.
          </p>

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={cfg.verImagenes}
                onChange={(e) => set({ verImagenes: e.target.checked })}
              />
              Mirar las imágenes
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={cfg.transcribirAudios}
                onChange={(e) => set({ transcribirAudios: e.target.checked })}
              />
              Transcribir los audios
            </label>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
